/**
 * The Games page (`/a/games`, ADR-035): every game on Fias, laid out like a
 * games store — featured, new, top, genres, search — readable with no account.
 *
 * The page itself is `@fias/public-site-ui`: presentational components that
 * also render the static snapshots crawlers read, so what a crawler indexes
 * and what a visitor sees are the same markup from the same data. This file
 * only supplies what the app knows: the router's links, the data, whether the
 * visitor is signed in, and the frame — the app's own header for a signed-in
 * user, the public site's header and footer for everyone else.
 */
import { useMemo, type ReactElement } from 'react';
import { Link, Route, Routes } from 'react-router-dom';
import {
  GamesSiteProvider,
  GamesSurface,
  PublicSiteFrame,
  type SiteLinkProps,
} from '@fias/public-site-ui';
import { useAuth } from '@/auth/hooks/useAuth';
import { useGamesPageAvailable } from '@/lib/games-availability';
import { GAME_GENRE_OPTIONS } from '@/lib/game-genres';
import { ArcheLayout } from '../../components/ArcheLayout';
import { GamesHomePage } from './pages/GamesHomePage';
import { GameGenrePage } from './pages/GameGenrePage';
import { GameDetailPage, GamesNotFoundPage } from './pages/GameDetailPage';
import { GameArchePage } from './pages/GameArchePage';
import { GamesLoading } from './pages/GamesStatus';
import { GamesUnavailablePage } from './pages/GamesUnavailablePage';

/** The router's link, drawn with the package's link props (same markup as `<a>`). */
function RouterLink({ href, children, ...rest }: SiteLinkProps): ReactElement {
  return (
    <Link to={href} {...rest}>
      {children}
    </Link>
  );
}

const GENRE_LABELS: Readonly<Record<string, string>> = Object.fromEntries(
  GAME_GENRE_OPTIONS.map((g) => [g.id, g.label]),
);

function GamesRoutes(): ReactElement {
  return (
    <Routes>
      <Route index element={<GamesHomePage />} />
      <Route path="genre/:genreId" element={<GameGenrePage />} />
      <Route path="game/:slug" element={<GameDetailPage />} />
      <Route path="from/:archeKey" element={<GameArchePage />} />
      <Route path="*" element={<GamesNotFoundPage />} />
    </Routes>
  );
}

export function GamesArchePage(): ReactElement {
  const { isAuthenticated } = useAuth();
  // Hidden in this environment (the Dev Console switch): no catalog, and no
  // site frame that would link to it.
  const available = useGamesPageAvailable();
  // "Now" for the page's lifetime: what "new" is measured from.
  const now = useMemo(() => Date.now(), []);

  const body =
    available === null ? <GamesLoading /> : available ? <GamesRoutes /> : <GamesUnavailablePage />;
  const site = (
    <GamesSiteProvider
      Link={RouterLink}
      signedIn={isAuthenticated}
      now={now}
      genreLabels={GENRE_LABELS}
    >
      <GamesSurface>
        {isAuthenticated || !available ? (
          body
        ) : (
          <PublicSiteFrame current="games">{body}</PublicSiteFrame>
        )}
      </GamesSurface>
    </GamesSiteProvider>
  );

  if (isAuthenticated) {
    return (
      <ArcheLayout displayMode="normal" showSidebar={false}>
        {site}
      </ArcheLayout>
    );
  }
  return site;
}

export default GamesArchePage;
